import { ExactTheme } from 'styles/theme';
import { useThemeUI } from 'theme-ui';
import { useEffect, useState } from 'react';
import useBreakpoint from 'utils/hooks/useBreakpoint';

export type AnchorNavData = {
  label: string;
  id: string;
  sxOverride?: ExactTheme;
};

export interface AnchorNavProps {
  navData: AnchorNavData[];
  offset?: number;
}

export function scrollToElement(id: string, offset: number) {
  const element = document.getElementById(id);
  if (element) {
    const top = element.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: top - offset,
      behavior: 'smooth'
    });
  }
}

const AnchorNav = ({ navData, offset }: AnchorNavProps) => {
  const breakpoint = useBreakpoint();
  const ctx = useThemeUI();
  const theme = ctx.theme as ExactTheme;
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      let maxIntersect = {
        id: '',
        intersectionRatio: 0
      };

      entries.forEach(entry => {
        if (
          entry.isIntersecting &&
          entry.intersectionRatio > maxIntersect.intersectionRatio
        ) {
          maxIntersect = {
            id: entry.target.id,
            intersectionRatio: entry.intersectionRatio
          };
        }
      });

      if (maxIntersect.id) {
        setActiveSection(maxIntersect.id);
      }
    });

    navData.forEach(data => {
      const section = document.getElementById(data.id);
      if (section) {
        observer.observe(section);
      }
    });
  }, [navData]);

  const baseStyle = {
    display: 'block',
    content: '""',
    margin: '0 auto'
  };

  return (
    <div
      className="anchorNav"
      sx={{
        width: '100vw',
        position: 'sticky',
        overflow: 'hidden',
        top: '0',
        zIndex: '3',
        margin: '0',
        boxShadow: '0 0 0.9375rem 0 rgba(0, 0, 0, 0.20)',
        backgroundColor: `${theme?.colors?.white}`
      }}
    >
      <nav aria-label="Section">
        <ul
          sx={{
            overflowX: 'auto',
            display: 'flex',
            justifyContent: ['center', 'flex-start', 'flex-start'],
            alignItems: 'center',
            columnGap: '1.5rem',
            listStyle: 'none',
            padding: '0 2rem',
            margin: '0'
          }}
        >
          {navData?.map((data: AnchorNavData) => (
            <li
              key={data.id}
              sx={{
                display: 'inline-block',
                whiteSpace: breakpoint.isLessThanMediumMin
                  ? 'nowrap'
                  : 'normal',
                maxWidth: 'fit-content',
                textAlign: 'center',
                padding: '1rem 0 0.625rem 0',
                borderBottom:
                  data.id === activeSection
                    ? `0.375rem solid ${theme?.colors?.brandOrangeWeb}`
                    : '0.375rem solid transparent',
                '&:hover, &:active': {
                  borderBottom: 'none',
                  padding: '1rem 0 0 0'
                },
                '&:hover:after, &:active:after': {
                  ...baseStyle,
                  padding: '1rem 0 0 0',
                  borderBottom: `0.375rem solid ${theme?.colors?.brandOrangeWeb}`
                },
                '&:hover:before': {
                  width: '0'
                },
                '&:after': {
                  ...baseStyle,
                  width: '0',
                  borderBottom: `0.375rem solid ${theme?.colors?.brandOrangeWeb}`
                },
                '&:hover:after': {
                  width: data.id === activeSection ? '100%' : '1.5rem',
                  transition:
                    data.id === activeSection ? 'none' : 'width 0.3s ease'
                },
                '&:focus': {
                  outline: `none`
                },
                '&:focus-visible:after': {
                  outline: `0.125rem solid ${theme?.colors?.extendedBlue100}`
                },
                '&:active:after': {
                  transition: 'width 0.3s ease',
                  width: '100%'
                }
              }}
            >
              <a
                href={`#${data.id}`}
                className="anchorNavLink"
                onClick={e => {
                  e.preventDefault();
                  scrollToElement(data.id, offset ?? 70);
                }}
                sx={{
                  textDecoration: 'none',
                  display: 'block',
                  padding: '0 1rem',
                  fontWeight: '600',
                  color:
                    data.id === activeSection
                      ? `${theme?.colors?.brandNavy100}`
                      : `${theme?.colors?.darkGreyText}`,
                  '&:hover': {
                    display: 'block',
                    textDecoration: 'none',
                    color: `${theme?.colors?.darkGreyText}`,
                    padding: '0 1rem'
                  },
                  '&:active': {
                    textDecoration: 'none',
                    padding: '0 1rem',
                    transition: 'none',
                    color: `${theme?.colors?.brandNavy100}`
                  }
                }}
              >
                {data.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

export default AnchorNav;
